import { defineStore } from "pinia";
import type { Service } from "@/constants/cloud-apps";
import api from "@/_helpers/api";
import { axiosInstance } from "@/plugins/https";
import {
  convertIpBlocklistPayloadForBE,
  convertPayloadForBE,
  convertThreatTypesPayloadForBE,
  normalizeIpBlocklist,
  normalizePolicies,
} from "@/_store/cloud-apps/adapters";
import { useSnackbarStore } from "@/_store/snackbar.module";
import { i18n } from "@/plugins/i18n";
import { StoreModule } from "@/constants/store.ts";
import { TicketTrigger } from "@/constants/tickets.ts";
import { AccessPermissionLocationPanel, AccessPermissionType } from "@/constants/cloud-apps";
import type { UserLabel } from "@/_store/users-settings/user-labels.module.ts";

export interface Time {
  hours: number;
  minutes: number;
}

export type AccessPermissionRemediation = "none" | "suspend" | "signin";
export type AccessPermissionRestrictionBy = "country" | "ip" | "time";

export type ServiceSelection = "all" | Service;

export interface AccessPermissionItem {
  id: string;
  serviceName: ServiceSelection;
  userType: AccessPermissionType;
  groups: Group[];
  labels: UserLabel[];
  users: MonitoredScopeUser[];
  type: AccessPermissionRestrictionBy;
  allowedLocations: string[];
  allowedIps: string[];
  description?: string;
  allowedDays: number[];
  allowedStartTime: string;
  allowedEndTime: string;
  remediation: AccessPermissionRemediation;
  locationPanel?: AccessPermissionLocationPanel;
}

export interface AccessPermissionItemRaw extends Omit<
  AccessPermissionItem,
  "allowedStartTime" | "allowedEndTime"
> {
  allowedStartTime: Time | null;
  allowedEndTime: Time | null;
}

export interface ThreatTypeItem {
  id: string;
  type: TicketTrigger;
  userType: AccessPermissionType;
  groups: Group[];
  labels: UserLabel[];
  users: MonitoredScopeUser[];
  serviceName: ServiceSelection;
  remediation: AccessPermissionRemediation;
}

export type MonitoredScopeUser = { userId?: string; email: string; service?: Service };

export interface Group {
  groupId: string;
  groupName: string;
  serviceName: Service;
  name?: string;
  service?: Service;
}

export interface IpBlocklistItem {
  id: string;
  userType: AccessPermissionType;
  groups: Group[];
  users: MonitoredScopeUser[];
  labels: UserLabel[];
  blockedIps: string[];
  remediation: AccessPermissionRemediation;
  description?: string;
}

export interface AccessPermissionsState {
  policies: AccessPermissionItem[];
  ipBlocklist: AccessPermissionItem[];
  threatTypes: ThreatTypeItem[];
  accessPermissionsLoading: boolean;
  ipBlocklistLoading: boolean;
  threatTypesLoading: boolean;
}

export const useAccessPermissionsStore = defineStore(StoreModule.ACCESS_PERMISSIONS, {
  state: (): AccessPermissionsState => ({
    policies: [],
    ipBlocklist: [],
    threatTypes: [],
    accessPermissionsLoading: false,
    ipBlocklistLoading: false,
    threatTypesLoading: false,
  }),
  actions: {
    async init() {
      await this.getAccessPermissions();
      await this.getIpBlocklist();
      await this.getThreatTypes();
    },
    async getAccessPermissions() {
      const request = {
        ...api.restrictions(),
      };

      try {
        const { data } = await axiosInstance.request(request);

        this.policies = normalizePolicies(data);
      } catch (e) {
        console.error(e);
      }
    },
    async getIpBlocklist() {
      try {
        const { data } = await axiosInstance.request({ ...api.ipBlocklist() });
        this.ipBlocklist = normalizeIpBlocklist(data);
      } catch (e) {
        console.error(e);
      }
    },
    async addIpBlocklistEntry(payload: AccessPermissionItem) {
      this.ipBlocklistLoading = true;
      try {
        await axiosInstance.request({
          ...api.ipBlocklist(),
          method: "post",
          data: convertIpBlocklistPayloadForBE(payload),
        });
        await this.getIpBlocklist();
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.accessPermissions.accessPermissionsUpdated")
        );
      } catch (e) {
        console.error(e);
      } finally {
        this.ipBlocklistLoading = false;
      }
    },
    async editIpBlocklistEntry(payload: AccessPermissionItem) {
      this.ipBlocklistLoading = true;
      try {
        await axiosInstance.request({
          ...api.ipBlocklist(),
          method: "put",
          params: { id: payload.id },
          data: convertIpBlocklistPayloadForBE(payload),
        });
        await this.getIpBlocklist();
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.accessPermissions.accessPermissionsUpdated")
        );
      } catch (e) {
        console.error(e);
      } finally {
        this.ipBlocklistLoading = false;
      }
    },
    async deleteIpBlocklistEntry(payload: AccessPermissionItem) {
      this.ipBlocklistLoading = true;
      try {
        await axiosInstance.request({
          ...api.ipBlocklist(),
          url: `${api.ipBlocklist().url}/${payload.id}`,
          method: "delete",
        });
        await this.getIpBlocklist();
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.accessPermissions.accessPermissionsUpdated")
        );
      } catch (e) {
        console.error(e);
      } finally {
        this.ipBlocklistLoading = false;
      }
    },
    async getThreatTypes() {
      const request = {
        ...api.threatTypes(),
      };

      try {
        const { data } = await axiosInstance.request(request);

        this.threatTypes = data;
      } catch (e) {
        console.error(e);
      }
    },
    async addAccessPermissions(payload: AccessPermissionItem) {
      const request = {
        ...api.restrictions(),
        method: "post",
        data: {
          ...convertPayloadForBE(payload),
        },
      };

      this.accessPermissionsLoading = true;

      try {
        await axiosInstance.request(request);
        await this.getAccessPermissions();
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.accessPermissions.accessPermissionsUpdated")
        );
      } catch (e) {
        console.error(e);
      } finally {
        this.accessPermissionsLoading = false;
      }
    },
    async editAccessPermissions(payload: AccessPermissionItem) {
      const request = {
        ...api.restrictions(),
        method: "put",
        params: {
          id: payload.id,
        },
        data: convertPayloadForBE(payload),
      };

      this.accessPermissionsLoading = true;
      try {
        await axiosInstance.request(request);
        await this.getAccessPermissions();
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.accessPermissions.accessPermissionsUpdated")
        );
      } catch (e) {
        console.error(e);
      }
      this.accessPermissionsLoading = false;
    },
    async deleteAccessPermissions(payload: AccessPermissionItem) {
      const request = {
        ...api.restrictions(),
        params: {
          id: payload.id,
        },
        method: "delete",
      };
      this.accessPermissionsLoading = true;
      try {
        await axiosInstance.request(request);
        await this.getAccessPermissions();
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.accessPermissions.accessPermissionsUpdated")
        );
      } catch (e) {
        console.error(e);
      } finally {
        this.accessPermissionsLoading = false;
      }
    },
    async addThreatType(payload: ThreatTypeItem) {
      const request = {
        ...api.threatTypes(),
        method: "POST",
        data: {
          ...convertThreatTypesPayloadForBE(payload),
        },
      };

      this.threatTypesLoading = true;
      try {
        await axiosInstance.request(request);
        await this.getThreatTypes();
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.accessPermissions.threatTypesUpdated")
        );
      } catch (e) {
        console.error(e);
      }
      this.threatTypesLoading = false;
    },
    async editThreatType(payload: ThreatTypeItem) {
      const request = {
        ...api.threatTypes(),
        method: "put",
        params: {
          id: payload.id,
        },
        data: convertThreatTypesPayloadForBE(payload),
      };
      this.threatTypesLoading = true;
      try {
        await axiosInstance.request(request);
        await this.getThreatTypes();
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.accessPermissions.threatTypesUpdated")
        );
      } catch (e) {
        console.error(e);
      }
      this.threatTypesLoading = false;
    },
    async deleteThreatType(payload: ThreatTypeItem) {
      const request = {
        ...api.threatTypes(),
        params: {
          id: payload.id,
        },
        method: "delete",
      };
      this.threatTypesLoading = true;
      try {
        await axiosInstance.request(request);
        await this.getThreatTypes();
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.accessPermissions.threatTypesUpdated")
        );
      } catch (e) {
        console.error(e);
      } finally {
        this.threatTypesLoading = false;
      }
    },
    async searchProtectedUsers({
      email,
      serviceName,
    }: {
      email: string;
      serviceName: ServiceSelection;
    }): Promise<MonitoredScopeUser[]> {
      const request = {
        ...api.users(),
        params: {
          pageSize: 10,
          page: 0,
          email,
          cloudServices: serviceName === "all" ? null : serviceName,
        },
      };
      const { data } = await axiosInstance.request(request);
      return data.items.map((u: Partial<MonitoredScopeUser>) => {
        return {
          userId: u.userId,
          email: u.email,
          service: serviceName,
        };
      });
    },
    async searchLabels(name: string): Promise<UserLabel[]> {
      const request = {
        ...api.searchUserLabels(),
        params: {
          search: name,
        },
      };
      const { data } = await axiosInstance.request(request);
      return data;
    },
    async searchGroups({
      limit,
      searchTerm,
      service,
    }: {
      searchTerm: string;
      limit: number;
      service: ServiceSelection;
    }): Promise<Group[]> {
      const request = {
        ...api.searchGroups(searchTerm, service, limit),
      };
      const { data } = await axiosInstance.request(request);
      return data.map((v: { groupId: string; name: string; service: Service }) => {
        return {
          ...v,
          serviceName: v.service,
          groupName: v.name,
        };
      });
    },
  },
});
